/**
 * MDM Helper Utilities
 *
 * Purpose: Pure utility functions for Master Data Management operations
 * Architecture: Stateless helper functions for transformation, filtering, and hierarchy logic
 * Dependencies: MDM types, Field Definition types
 * Author: Development Team
 * Created: 2025-01-21
 */

import React, { ReactNode } from 'react';
import { Public as GlobalIcon, Business as TenantIcon } from '@mui/icons-material';
import { MasterDataValue, HierarchyPathItem } from '../types/mdm';
import { FieldDefinition } from '../services/metadataService';

/**
 * Filter field definitions to show only list types with deduplication and hierarchical labeling
 *
 * Purpose: Prepare field definitions for display in dropdown selectors
 * Logic:
 * - Filters to only include 'list' datatype field definitions
 * - Deduplicates by fielddef_id to handle duplicate definitions
 * - Sorts hierarchical fields by hierarchy label and level
 * - Sorts non-hierarchical fields alphabetically
 *
 * @param fieldDefinitions - Array of field definitions to filter
 * @returns Filtered, deduplicated, and sorted field definitions
 *
 * @example
 * const dropdownOptions = getDropdownFieldDefinitions(allFieldDefs);
 */
export function getDropdownFieldDefinitions(
  fieldDefinitions: FieldDefinition[]
): FieldDefinition[] {
  // Filter to only list types
  const filtered = fieldDefinitions.filter((fd) => {
    const dataType = fd.datatype?.toLowerCase() || '';
    return dataType === 'list';
  });

  // Deduplicate by fielddef_id
  const uniqueFieldDefs = new Map<string, FieldDefinition>();
  filtered.forEach((fd) => {
    if (!uniqueFieldDefs.has(fd.fielddef_id)) {
      uniqueFieldDefs.set(fd.fielddef_id, fd);
    }
  });

  const fieldDefsArray = Array.from(uniqueFieldDefs.values());

  // Sort: hierarchical fields by hierarchy label and level, others alphabetically
  const sorted = fieldDefsArray.sort((a, b) => {
    const aIsHierarchical = a.hierarchy_config !== null && a.hierarchy_config !== undefined;
    const bIsHierarchical = b.hierarchy_config !== null && b.hierarchy_config !== undefined;

    const aHierarchyLabel = aIsHierarchical ? a.hierarchy_config?.hierarchy_label : null;
    const bHierarchyLabel = bIsHierarchical ? b.hierarchy_config?.hierarchy_label : null;

    // If both have the same hierarchy label, sort by hierarchy level
    if (aHierarchyLabel && bHierarchyLabel && aHierarchyLabel === bHierarchyLabel) {
      const aLevel = a.hierarchy_config?.hierarchy_level || 0;
      const bLevel = b.hierarchy_config?.hierarchy_level || 0;
      if (aLevel !== bLevel) {
        return aLevel - bLevel;
      }
      // Same level - sort by field label
      return a.label.localeCompare(b.label);
    }

    // Otherwise, sort alphabetically by display label
    const aDisplayLabel =
      aIsHierarchical && aHierarchyLabel ? `${aHierarchyLabel} - ${a.label}` : a.label;
    const bDisplayLabel =
      bIsHierarchical && bHierarchyLabel ? `${bHierarchyLabel} - ${b.label}` : b.label;

    return aDisplayLabel.localeCompare(bDisplayLabel);
  });

  return sorted;
}

/**
 * Get display label for a field definition (with hierarchy label if applicable)
 *
 * Purpose: Generate consistent display labels for hierarchical and non-hierarchical fields
 *
 * @param fieldDefinition - Field definition to get label for
 * @returns Display label string
 *
 * @example
 * const label = getFieldDefinitionDisplayLabel(fieldDef);
 * // Returns: "Geography - Region" for hierarchical field
 * // Returns: "Status" for non-hierarchical field
 */
export function getFieldDefinitionDisplayLabel(fieldDefinition: FieldDefinition): string {
  const isHierarchical =
    fieldDefinition.hierarchy_config !== null && fieldDefinition.hierarchy_config !== undefined;

  if (isHierarchical && fieldDefinition.hierarchy_config?.hierarchy_label) {
    return `${fieldDefinition.hierarchy_config.hierarchy_label} - ${fieldDefinition.label}`;
  }

  return fieldDefinition.label;
}

/**
 * Extract ancestor field definitions from values' hierarchy paths
 *
 * Purpose: Get unique ancestor field definitions for hierarchical field values
 * Logic:
 * - Extracts all unique ancestor fielddef_ids from hierarchy_path arrays
 * - Excludes the current field definition ID
 * - Returns matching field definitions
 *
 * @param values - Array of master data values with hierarchy paths
 * @param selectedFieldDefId - Current field definition ID to exclude
 * @param allFieldDefinitions - All available field definitions
 * @returns Array of ancestor field definitions
 *
 * @example
 * const ancestors = getAncestorFieldDefinitions(values, 'region-id', allFieldDefs);
 */
export function getAncestorFieldDefinitions(
  values: MasterDataValue[],
  selectedFieldDefId: string,
  allFieldDefinitions: FieldDefinition[]
): FieldDefinition[] {
  if (!values.length) {
    return [];
  }

  // Extract all unique ancestor fielddef_ids (excluding current fielddef_id)
  const ancestorFieldDefIds = new Set<string>();
  values.forEach((value) => {
    if (value.hierarchy_path && value.hierarchy_path.length > 0) {
      value.hierarchy_path.forEach((item: HierarchyPathItem) => {
        // Exclude the current fielddef_id (the value itself)
        if (item.fielddef_id !== selectedFieldDefId) {
          ancestorFieldDefIds.add(item.fielddef_id);
        }
      });
    }
  });

  // Get field definitions for ancestor fielddef_ids
  return allFieldDefinitions.filter((fd) => ancestorFieldDefIds.has(fd.fielddef_id));
}

/**
 * Get ancestor order from values' hierarchy paths
 *
 * Purpose: Determine the correct order of ancestor field definitions from hierarchy paths
 * Logic: Uses the first value with a hierarchy_path to establish canonical order
 *
 * @param values - Array of master data values with hierarchy paths
 * @param selectedFieldDefId - Current field definition ID to exclude
 * @returns Ordered array of ancestor fielddef_ids
 *
 * @example
 * const order = getAncestorOrder(values, 'city-id');
 * // Returns: ['country-id', 'region-id']
 */
export function getAncestorOrder(values: MasterDataValue[], selectedFieldDefId: string): string[] {
  const firstValueWithPath = values.find((v) => v.hierarchy_path && v.hierarchy_path.length > 0);
  const ancestorOrder: string[] = [];

  if (firstValueWithPath?.hierarchy_path) {
    // Extract fielddef_ids in order (excluding the current fielddef_id)
    firstValueWithPath.hierarchy_path.forEach((item: HierarchyPathItem) => {
      if (item.fielddef_id !== selectedFieldDefId && !ancestorOrder.includes(item.fielddef_id)) {
        ancestorOrder.push(item.fielddef_id);
      }
    });
  }

  return ancestorOrder;
}

/**
 * Sort ancestor field definitions by hierarchy order
 *
 * Purpose: Sort ancestors in the correct hierarchy order (root to parent)
 *
 * @param ancestorFieldDefs - Array of ancestor field definitions
 * @param ancestorOrder - Canonical order from hierarchy paths
 * @returns Sorted array of ancestor field definitions
 *
 * @example
 * const sorted = sortAncestorFieldDefinitions(ancestors, ['country-id', 'region-id']);
 */
export function sortAncestorFieldDefinitions(
  ancestorFieldDefs: FieldDefinition[],
  ancestorOrder: string[]
): FieldDefinition[] {
  return [...ancestorFieldDefs].sort((a, b) => {
    const aIndex = ancestorOrder.indexOf(a.fielddef_id);
    const bIndex = ancestorOrder.indexOf(b.fielddef_id);

    // If not found in order, fall back to hierarchy_level
    if (aIndex === -1 && bIndex === -1) {
      const aLevel = a.hierarchy_config?.hierarchy_level || 0;
      const bLevel = b.hierarchy_config?.hierarchy_level || 0;
      return aLevel - bLevel;
    }
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
}

/**
 * Get ancestor value name from hierarchy path
 *
 * Purpose: Extract ancestor value name for a specific field definition from hierarchy path
 *
 * @param hierarchyPath - Value's hierarchy path
 * @param ancestorFieldDefId - Field definition ID to find in path
 * @returns Ancestor value name or null if not found
 *
 * @example
 * const regionName = getAncestorValueName(value.hierarchy_path, 'region-id');
 */
export function getAncestorValueName(
  hierarchyPath: HierarchyPathItem[] | null | undefined,
  ancestorFieldDefId: string
): string | null {
  if (!hierarchyPath || hierarchyPath.length === 0) {
    return null;
  }

  const ancestorItem = hierarchyPath.find(
    (item: HierarchyPathItem) => item.fielddef_id === ancestorFieldDefId
  );

  return ancestorItem?.name || null;
}

/**
 * Create chip color lookup map from values
 *
 * Purpose: Build a lookup map for chip colors by fielddef_id and value name
 * Logic: Fallback for cases where chip_color isn't in resolved data
 *
 * @param values - Array of master data values
 * @returns Map with keys as "fielddef_id|name" and values as chip_color
 *
 * @example
 * const lookupMap = createChipColorLookupMap(values);
 * const color = lookupMap.get('status-id|Active');
 */
export function createChipColorLookupMap(values: MasterDataValue[]): Map<string, string> {
  const map = new Map<string, string>();

  values.forEach((value) => {
    if (value.chip_color) {
      const key = `${value.fielddef_id}|${value.name}`;
      map.set(key, value.chip_color);
    }
  });

  return map;
}

/**
 * Lookup chip color for a specific field definition and value name
 *
 * Purpose: Get chip color from lookup map
 *
 * @param chipColorMap - Chip color lookup map
 * @param fielddefId - Field definition ID
 * @param valueName - Value name
 * @returns Chip color string or null if not found
 *
 * @example
 * const color = lookupChipColor(colorMap, 'status-id', 'Active');
 */
export function lookupChipColor(
  chipColorMap: Map<string, string>,
  fielddefId: string,
  valueName: string
): string | null {
  const key = `${fielddefId}|${valueName}`;
  return chipColorMap.get(key) || null;
}

/**
 * Get scope icon for a given scope value
 *
 * Purpose: Return appropriate Material-UI icon for scope
 *
 * @param scope - Scope value ('GLOBAL', 'LOCAL', 'OVERRIDDEN')
 * @returns React icon element or undefined
 *
 * @example
 * const icon = getScopeIcon('GLOBAL');
 * // Returns: React.createElement(GlobalIcon, { fontSize: "small" })
 */
export function getScopeIcon(scope: string): ReactNode | undefined {
  switch (scope) {
    case 'GLOBAL':
      return React.createElement(GlobalIcon, { fontSize: 'small' });
    case 'LOCAL':
      return React.createElement(TenantIcon, { fontSize: 'small' });
    case 'OVERRIDDEN':
      return React.createElement(TenantIcon, { fontSize: 'small' });
    default:
      return undefined;
  }
}

/**
 * Check if a value is a tenant override
 *
 * Purpose: Determine if a value is an override of a global value
 *
 * @param value - Master data value
 * @returns True if value is an override, false otherwise
 *
 * @example
 * const isOverride = isTenantOverride(value);
 */
export function isTenantOverride(value: MasterDataValue): boolean {
  return value.scope_info.scope === 'OVERRIDDEN';
}

/**
 * Check if a value is a global value
 *
 * Purpose: Determine if a value is defined at global scope
 *
 * @param value - Master data value
 * @returns True if value is global, false otherwise
 *
 * @example
 * const isGlobal = isGlobalValue(value);
 */
export function isGlobalValue(value: MasterDataValue): boolean {
  return value.scope_info.scope === 'GLOBAL';
}

/**
 * Check if a value is a local (tenant-specific) value
 *
 * Purpose: Determine if a value is defined only for a specific tenant
 *
 * @param value - Master data value
 * @returns True if value is local, false otherwise
 *
 * @example
 * const isLocal = isLocalValue(value);
 */
export function isLocalValue(value: MasterDataValue): boolean {
  return value.scope_info.scope === 'LOCAL';
}

/**
 * Filter values based on search term and scope filter
 *
 * Purpose: Apply client-side filtering to master data values
 * Logic:
 * - Filters by name (case-insensitive search)
 * - Filters by scope if scope filter is set and not 'ALL'
 *
 * @param values - Array of master data values
 * @param searchTerm - Search term to filter by name
 * @param scopeFilter - Optional scope filter ('GLOBAL', 'LOCAL', 'OVERRIDDEN', or 'ALL')
 * @returns Filtered array of values
 *
 * @example
 * const filtered = filterValues(allValues, 'active', 'GLOBAL');
 */
export function filterValues(
  values: MasterDataValue[],
  searchTerm: string,
  scopeFilter?: string
): MasterDataValue[] {
  let filtered = values;

  // Apply search filter
  if (searchTerm) {
    filtered = filtered.filter((value) =>
      value.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Apply scope filter
  if (scopeFilter && scopeFilter !== 'ALL') {
    filtered = filtered.filter((value) => value.scope_info.scope === scopeFilter);
  }

  return filtered;
}

/**
 * Validate if field definition and tenant selection is valid
 *
 * Purpose: Check if both required selections are made
 *
 * @param selectedFieldDef - Selected field definition ID
 * @param selectedTenant - Selected tenant ID
 * @returns True if both selections are valid, false otherwise
 *
 * @example
 * const isValid = isValidSelection('field-id', 'tenant-id');
 */
export function isValidSelection(
  selectedFieldDef: string | undefined | null,
  selectedTenant: string | undefined | null
): boolean {
  return Boolean(selectedFieldDef && selectedTenant);
}

/**
 * Check if a field definition is hierarchical
 *
 * Purpose: Determine if a field definition has hierarchy configuration
 *
 * @param fieldDefinition - Field definition to check
 * @returns True if hierarchical, false otherwise
 *
 * @example
 * const isHierarchical = isHierarchicalField(fieldDef);
 */
export function isHierarchicalField(fieldDefinition: FieldDefinition | undefined | null): boolean {
  if (!fieldDefinition) return false;
  return (
    fieldDefinition.hierarchy_config !== null && fieldDefinition.hierarchy_config !== undefined
  );
}

/**
 * Check if a field definition is a source field
 *
 * Purpose: Determine if a field definition displays values from another field
 *
 * @param fieldDefinition - Field definition to check
 * @returns True if source field, false otherwise
 *
 * @example
 * const isSource = isSourceField(fieldDef);
 */
export function isSourceField(fieldDefinition: FieldDefinition | undefined | null): boolean {
  if (!fieldDefinition) return false;
  return fieldDefinition.sourcevalue_fielddef_id != null;
}

/**
 * Get effective field definition for columns
 *
 * Purpose: For source fields, return the source field definition; otherwise return the field itself
 * Logic: Source fields should display columns from their source field definition
 *
 * @param fieldDefinition - Current field definition
 * @param allFieldDefinitions - All available field definitions
 * @returns Effective field definition for column display
 *
 * @example
 * const effectiveFieldDef = getEffectiveFieldDefinition(fieldDef, allFieldDefs);
 */
export function getEffectiveFieldDefinition(
  fieldDefinition: FieldDefinition | undefined | null,
  allFieldDefinitions: FieldDefinition[]
): FieldDefinition | undefined | null {
  if (!fieldDefinition) return null;

  if (isSourceField(fieldDefinition) && fieldDefinition.sourcevalue_fielddef_id) {
    // Find the source field definition
    return allFieldDefinitions.find(
      (fd) => fd.fielddef_id === fieldDefinition.sourcevalue_fielddef_id
    );
  }

  return fieldDefinition;
}

/**
 * Check if deletion is allowed for a value based on scope and context
 *
 * Purpose: Validate deletion permissions based on business rules
 * Logic:
 * - GLOBAL values can only be deleted in GLOBAL workspace
 * - OVERRIDDEN values cannot be deleted (must be reverted)
 * - LOCAL values can be deleted in their tenant context
 *
 * @param value - Master data value to check
 * @param currentTenantId - Current tenant context ID
 * @returns Object with allowed flag and reason message if not allowed
 *
 * @example
 * const { allowed, reason } = canDeleteValue(value, 'tenant-123');
 * if (!allowed) alert(reason);
 */
export function canDeleteValue(
  value: MasterDataValue,
  currentTenantId: string
): { allowed: boolean; reason?: string } {
  const { scope } = value.scope_info;

  // GLOBAL values can only be deleted in GLOBAL workspace
  if (scope === 'GLOBAL' && currentTenantId !== 'GLOBAL') {
    return {
      allowed: false,
      reason: 'Global values can only be deleted in the GLOBAL workspace',
    };
  }

  // Overrides cannot be deleted - only reverted
  if (scope === 'OVERRIDDEN') {
    return {
      allowed: false,
      reason: 'Override values cannot be deleted. Use the Revert action instead.',
    };
  }

  return { allowed: true };
}

/**
 * Build hierarchy chain from field definition ID
 *
 * Purpose: Construct full hierarchy chain from root to specified field definition
 * Logic:
 * - Follows parent_fielddef_id references
 * - Builds chain from root to target
 * - Returns array ordered from root to target
 *
 * @param fieldDefId - Target field definition ID
 * @param allFieldDefinitions - All available field definitions
 * @returns Array of field definitions in hierarchy order (root to target)
 *
 * @example
 * const chain = buildHierarchyChain('city-id', allFieldDefs);
 * // Returns: [countryFieldDef, regionFieldDef, cityFieldDef]
 */
export function buildHierarchyChain(
  fieldDefId: string,
  allFieldDefinitions: FieldDefinition[]
): FieldDefinition[] {
  const chain: FieldDefinition[] = [];
  let currentFieldDef = allFieldDefinitions.find((fd) => fd.fielddef_id === fieldDefId);

  // Build chain by following parent references
  while (currentFieldDef) {
    chain.unshift(currentFieldDef); // Add to beginning to maintain root-to-leaf order

    // Find parent
    const parentId = currentFieldDef.hierarchy_config?.parent_fielddef_id;
    if (parentId) {
      currentFieldDef = allFieldDefinitions.find((fd) => fd.fielddef_id === parentId);
    } else {
      break; // Reached root
    }
  }

  return chain;
}

/**
 * Resolve parent value information from hierarchy path
 *
 * Purpose: Extract parent value details from a value's hierarchy path
 *
 * @param value - Master data value with hierarchy path
 * @returns Parent value information or null if no parent
 *
 * @example
 * const parentInfo = resolveParentValue(value);
 * // Returns: { fielddef_id: 'region-id', id: 'region-123', name: 'West Region' }
 */
export function resolveParentValue(value: MasterDataValue): HierarchyPathItem | null {
  if (!value.hierarchy_path || value.hierarchy_path.length === 0) {
    return null;
  }

  // Parent is the last item in the hierarchy path (before the value itself)
  const parentIndex = value.hierarchy_path.length - 1;
  return value.hierarchy_path[parentIndex];
}
